import { useState, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Play, X, Expand } from "lucide-react";
import { toast } from "sonner";
import { VIDEO_GALLERY } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";

const DEMO_VIDEO_SRC = "/founder-walkthrough.mp4";
const DEMO_POSTER = "/founder-walkthrough-poster.jpg";

const galleryImages = [
  {
    type: "rich",
    id: "beach-mode",
    picture: {
      full: "/images/gallery/pickle-wall-beach-mode.webp",
      thumb: "/images/gallery/pickle-wall-beach-mode-800.webp",
      width: 1500,
      height: 2000,
    },
    alt: "Pickle Wall portable pickleball training net set up on a Florida beach with a paddle and pickleballs, demonstrating the Anywhere. Anytime. pickleball lifestyle.",
    title: "Beach Mode",
    caption:
      "Pickle Wall goes wherever life takes you. Whether you're at the beach, the park, your driveway, or your garage, a few rallies are all it takes to stay connected to the game. Train a little. Relax a lot.",
    expanded: {
      headline: "Anywhere. Anytime.",
      description:
        "Pickle Wall isn't just for the garage. It's designed to travel with you. Whether you're spending the day at the beach, camping with family, tailgating before the game, or relaxing in your backyard, Pickle Wall makes it easy to enjoy a few rallies wherever you are. Every place becomes an opportunity to play, connect, and enjoy the sport.",
      categories: ["Lifestyle", "Anywhere. Anytime.", "Community"],
      tags: [
        "Beach Pickleball",
        "Portable Pickleball Net",
        "Pickle Wall",
        "Anywhere Anytime",
        "Pickleball Lifestyle",
        "Outdoor Training",
        "Vacation Pickleball",
        "Travel Pickleball",
        "Pickleball Training",
        "Mobile Pickleball",
      ],
    },
  },
  {
    type: "simple",
    src: "/garage-setup.jpg",
    alt: "Pickle Wall training setup mounted in a home garage",
    label: "Garage Setup",
  },
  {
    type: "simple",
    src: "https://customer-assets.emergentagent.com/job_pickle-trainer-v1/artifacts/9lg3tdlm_image.png",
    alt: "Father and son wearing Pickle Wall gear, celebrating a training win",
    label: "Train Together",
  },
  {
    type: "simple",
    src: "https://customer-assets.emergentagent.com/job_pickle-trainer-v1/artifacts/nhrgkl37_909E78DD-50BC-443C-A0F6-0D3584DA988D.PNG",
    alt: "Player warming up on a packed public court using Pickle Wall",
    label: "Court Warm-Up",
  },
  {
    type: "simple",
    src: "https://customer-assets.emergentagent.com/job_pickle-trainer-v1/artifacts/gqj8kh6b_IMG_0463.jpeg",
    alt: "Pickle Wall used as a court separator",
    label: "Court Separator",
  },
  {
    type: "simple",
    src: "https://customer-assets.emergentagent.com/job_pickle-trainer-v1/artifacts/ueobjmxb_IMG_E52AA85C-401B-4EC7-A377-2C85C5397B24.JPEG",
    alt: "Pickle Wall with kitchen lines laid out in a driveway",
    label: "Kitchen Lines",
  },
  {
    type: "simple",
    src: "https://customer-assets.emergentagent.com/job_pickle-trainer-v1/artifacts/tdl4oasr_08200C1B-E5B2-409A-B791-067DB4422F1A.PNG",
    alt: "Player training solo, getting reps in on the Pickle Wall",
    label: "Solo Reps",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const line = {
  hidden: { y: "110%" },
  visible: (i = 0) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function VideoGallery() {
  const [lightbox, setLightbox] = useState(null);
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: frameRef,
    offset: ["start end", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.06, 1, 1.06]);
  const y = useTransform(scrollYProgress, [0, 1], [-24, 24]);

  const handlePlay = () => {
    trackEvent("video_play", { button_text: "Watch Demo", button_location: "video-gallery" });
    if (!DEMO_VIDEO_SRC) {
      toast("Full walkthrough video dropping soon — stay tuned.", {
        description: "We're finishing final edits on the demo.",
      });
      return;
    }
    setLightbox({ kind: "video" });
  };

  return (
    <section
      id="demo"
      data-testid={VIDEO_GALLERY.section}
      className="relative py-24 md:py-36 bg-[#050505] overflow-hidden"
    >
      <div className="absolute -left-40 top-1/3 w-[560px] h-[560px] bg-[#FF6B00] opacity-[0.07] rounded-full blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="mx-auto mb-14 md:mb-20 max-w-3xl text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B00]"
          >
            See It In Action
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight text-balance leading-[0.95]"
          >
            <span className="block overflow-hidden">
              <motion.span variants={line} custom={0} className="block">
                Real Reps.
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                variants={line}
                custom={1}
                className="block text-[#FF6B00]"
              >
                Real Results.
              </motion.span>
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 text-lg text-white/70"
          >
            Watch the walkthrough, then browse real setups from the Pickle
            Wall community — no renders, no stock courts.
          </motion.p>
        </div>

        {/* Video frame */}
        <motion.div
          ref={frameRef}
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid={VIDEO_GALLERY.videoFrame}
          className="relative mb-16 md:mb-20 rounded-[28px] overflow-hidden border border-white/10 aspect-video bg-black"
        >
          <motion.img
            style={{ scale, y }}
            src={DEMO_POSTER}
            alt="Pickle Wall demo preview"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/40" />
          <button
            type="button"
            data-testid={VIDEO_GALLERY.playButton}
            onClick={handlePlay}
            aria-label="Play Pickle Wall demo video"
            className="group absolute inset-0 flex items-center justify-center"
          >
            <span className="relative flex items-center justify-center">
              <span className="absolute w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#FF6B00]/30 animate-ping" />
              <span className="relative w-20 h-20 md:w-24 md:h-24 rounded-full glass flex items-center justify-center group-hover:bg-[#FF6B00] group-hover:orange-glow-strong transition-all duration-300 group-hover:scale-105">
                <Play
                  size={28}
                  className="text-white group-hover:text-black fill-current transition-colors"
                />
              </span>
            </span>
          </button>
          <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/80 px-3 py-1.5 rounded-full glass">
              Founder Walkthrough
            </span>
          </div>
        </motion.div>

        {/* Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {galleryImages.map((img, i) => (
            <motion.button
              type="button"
              key={img.id || img.src}
              data-testid={VIDEO_GALLERY.galleryItem(i)}
              onClick={() => setLightbox({ kind: "image", ...img })}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              custom={i}
              aria-label={`Open ${img.title || img.label}`}
              className={`group relative overflow-hidden rounded-2xl border border-white/8 hover:border-[#FF6B00]/50 transition-colors bg-[#0f0f0f] ${
                i === 0 || i === 3 ? "aspect-square md:aspect-[4/5]" : "aspect-square"
              }`}
            >
              {img.type === "rich" ? (
                <picture className="absolute inset-0 block">
                  <source
                    type="image/webp"
                    srcSet={`${img.picture.thumb} 800w, ${img.picture.full} ${img.picture.width}w`}
                    sizes="(max-width: 640px) 92vw, (max-width: 1024px) 50vw, 34vw"
                  />
                  <img
                    src={img.picture.full}
                    alt={img.alt}
                    width={img.picture.width}
                    height={img.picture.height}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                  />
                </picture>
              ) : (
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-75 group-hover:opacity-90 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                    {img.title || img.label}
                  </span>
                  <Expand
                    size={16}
                    className="text-white/70 group-hover:text-[#FF6B00] transition-colors shrink-0"
                  />
                </div>
                {img.caption && (
                  <p className="text-[11px] text-white/70 leading-snug line-clamp-2 text-left">
                    {img.caption}
                  </p>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid={VIDEO_GALLERY.lightbox}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-5"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              data-testid={VIDEO_GALLERY.lightboxClose}
              onClick={() => setLightbox(null)}
              aria-label="Close"
              className="absolute right-5 top-5 md:right-8 md:top-8 w-11 h-11 rounded-full glass flex items-center justify-center hover:bg-[#FF6B00] transition-colors z-10"
            >
              <X size={20} className="text-white" />
            </button>

            {lightbox.kind === "video" ? (
              <motion.div
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[88vh] max-w-5xl w-full"
              >
                <video
                  className="w-full max-h-[88vh] aspect-video rounded-2xl bg-black object-contain"
                  controls
                  autoPlay
                  playsInline
                  poster={DEMO_POSTER}
                >
                  <source src={DEMO_VIDEO_SRC} type="video/mp4" />
                </video>
              </motion.div>
            ) : lightbox.expanded ? (
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.97, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                data-testid={VIDEO_GALLERY.expandedPanel}
                className="max-h-[88vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-[#0a0a0a] border border-white/10 flex flex-col md:flex-row"
              >
                <div className="md:w-2/5 shrink-0 aspect-[4/5] md:aspect-auto">
                  <img
                    src={lightbox.picture?.full || lightbox.src}
                    alt={lightbox.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 md:p-8 flex flex-col gap-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B00]">
                    {lightbox.title}
                  </div>
                  <h3
                    data-testid={VIDEO_GALLERY.expandedHeadline}
                    className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight leading-[0.95]"
                  >
                    {lightbox.expanded.headline}
                  </h3>
                  <p
                    data-testid={VIDEO_GALLERY.expandedDescription}
                    className="text-white/70 text-sm md:text-base leading-relaxed"
                  >
                    {lightbox.expanded.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {lightbox.expanded.categories.map((cat) => (
                      <span
                        key={cat}
                        data-testid={VIDEO_GALLERY.expandedCategory(cat)}
                        className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6B00] border border-[#FF6B00]/40 px-3 py-1.5 rounded-full whitespace-nowrap"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/10">
                    {lightbox.expanded.tags.map((tag) => (
                      <span
                        key={tag}
                        data-testid={VIDEO_GALLERY.expandedTag(tag)}
                        className="text-[10px] text-white/45 bg-white/[0.04] px-2.5 py-1 rounded-full whitespace-nowrap"
                      >
                        #{tag.replace(/\s+/g, "")}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[88vh] max-w-5xl w-full"
              >
                <img
                  src={lightbox.src}
                  alt={lightbox.alt}
                  className="max-h-[88vh] w-full rounded-2xl object-contain"
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
